// src/pages/profile-settings/patient/BloodType.jsx
import React from "react";
import { Search as SearchIcon, CircleX as CircleXIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import supabase from "@/services/supabaseClient";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Card from "@/components/ui/Card.jsx";
import SaveChip from "@/sot/primitives/SaveChip.jsx";
import Divider from "@/components/ui/Divider.jsx";
import Checkbox from "@/components/ui/Checkbox.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import { loadBloodTypes } from "@/utils/blood_types";
import { PATIENT_SETTINGS_PROFILE_ROUTE } from "@/pages/profile-settings/settingsNavigation.js";
import FolaOnboardingCard from "@/pages/profile-settings/FolaOnboardingCard.jsx";

import {
  loadPatientIntroPreview,
  savePatientIntroPreview,
} from "@/lib/patientIntroPreview";

const COMPLETION_SAFE_BLOOD_TYPES = ["Don't know", "Other"];

const FRENCH_SAFE_BLOOD_TYPES = {
  "Don't know": "Je ne sais pas",
  Other: "Autre",
};

function isFrenchLanguageCode(languageCode) {
  return String(languageCode || "").toLowerCase().startsWith("fr");
}

function getBloodTypeDisplayLabel(value, languageCode) {
  const text = String(value || "").trim();
  if (!isFrenchLanguageCode(languageCode)) return text;
  return FRENCH_SAFE_BLOOD_TYPES[text] || text;
}

/**
 * Patient Settings → Blood type
 * Mirrors StepBloodType but:
 * - Uses SettingsScreenShell + TopBarCenter + SaveChip
 * - Hydrates from patientIntroPreview.bloodType
 * - Single-select; stored as a string
 * - Shows "Changes saved" toast then auto-back
 * - Confirm dialog on close if there are unsaved changes
 */
export default function BloodTypeSettings() {
  const nav = useNavigate();
  const { refreshProfile } = useAuth() || {};
  const { t, languageCode } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);
  const preview = React.useMemo(() => loadPatientIntroPreview() || {}, []);
  const initialValue = (preview.bloodType || "").trim();

  const [all, setAll] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const [seedValue, setSeedValue] = React.useState(initialValue);
  const [selected, setSelected] = React.useState(initialValue);

  const [showConfirmLeave, setShowConfirmLeave] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  // Load blood types once
  React.useEffect(() => {
    let ok = true;
    (async () => {
      const rows = await loadBloodTypes(); // ["O+","A+","A-", ...]
      if (ok) setAll(rows);
    })();
    return () => {
      ok = false;
    };
  }, []);

  React.useEffect(() => {
    setSeedValue(initialValue);
    setSelected(initialValue);
  }, [initialValue]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateFromDbIfNeeded() {
      if (initialValue) return;

      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();
      if (!alive || authErr || !user?.id) return;

      const { data: row, error: rowErr } = await supabase
        .from("patient_profiles")
        .select("blood_type")
        .eq("user_id", user.id)
        .maybeSingle();
      if (!alive || rowErr || !row) return;

      const dbValue = String(row.blood_type || "").trim();
      if (!dbValue) return;

      setSeedValue(dbValue);
      setSelected(dbValue);
      savePatientIntroPreview({
        ...(loadPatientIntroPreview() || {}),
        bloodType: dbValue,
      });
    }

    hydrateFromDbIfNeeded();
    return () => {
      alive = false;
    };
  }, [initialValue]);

  const q = query.trim().toLowerCase();
  const options = React.useMemo(() => {
    const seen = new Set();
    const next = [];
    for (const option of [...COMPLETION_SAFE_BLOOD_TYPES, ...all]) {
      const canonical = String(option || "").trim();
      const key = canonical.toLowerCase();
      if (!canonical || seen.has(key)) continue;
      seen.add(key);
      next.push(canonical);
    }
    return next;
  }, [all]);
  const filtered = q
    ? options.filter((item) => item.toLowerCase().includes(q))
    : options;

  // Dirty tracking for Save chip + confirm dialog
  const dirty = (selected || "") !== (seedValue || "");
  const canSave = dirty && !!selected;

  const toggle = (t) => {
    setSelected((prev) => (prev === t ? "" : t));
  };

  async function handleSave() {
    if (!canSave) return;

    try {
      const current = loadPatientIntroPreview() || {};
      const next = {
        ...current,
        bloodType: selected,
      };
      savePatientIntroPreview(next);

      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();

      if (!authErr && user?.id) {
        const { error: upErr } = await supabase
          .from("patient_profiles")
          .upsert(
            {
              user_id: user.id,
              blood_type: selected,
            },
            { onConflict: "user_id" }
          );

        if (upErr) throw upErr;

        try {
          await refreshProfile?.();
        } catch {
          // non-fatal
        }
      }

      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        nav(PATIENT_SETTINGS_PROFILE_ROUTE);
      }, 2200);
    } catch {
      // keep UI calm; save snackbar state remains unchanged
    }
  }

  function handleClose() {
    if (!dirty) {
      nav(PATIENT_SETTINGS_PROFILE_ROUTE);
    } else {
      setShowConfirmLeave(true);
    }
  }

  return (
    <SettingsScreenShell>
      <TopBarCenter
        title={t("settings.subflows.bloodType.title", isFrench ? "Groupe sanguin" : "Blood type")}
        onBack={handleClose}
        close
        right={<SaveChip disabled={!canSave} onClick={handleSave} />}
      />

      <div className="mt-3">
        <FolaOnboardingCard role="patient" />
      </div>

      {/* Search row (matches blueprint) */}
      <div className="mt-3 flex items-center gap-3">
        <SearchIcon
          className="h-5 w-5 text-[rgb(var(--clr-on-surface-variant,86,106,139))]"
          aria-hidden
        />

        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("settings.subflows.bloodType.searchPlaceholder", "O+")}
          inputMode="search"
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="off"
          className="flex-1 bg-transparent outline-none border-0 focus:outline-none focus:ring-0
                     text-[16px] leading-6 placeholder:text-[rgb(var(--clr-on-surface-variant,86,106,139))]"
        />

        {!!q && (
          <button
            type="button"
            aria-label={t("settings.subflows.bloodType.clearSearch", isFrench ? "Effacer la recherche" : "Clear search")}
            onClick={() => setQuery("")}
            className="shrink-0 grid place-items-center"
            style={{
              width: 32,
              height: 32,
              borderRadius: 9999,
              WebkitTapHighlightColor: "transparent",
            }}
          >
            <CircleXIcon className="h-5 w-5 text-[rgb(var(--clr-on-surface-variant,86,106,139))]" />
          </button>
        )}
      </div>

      <Divider variant="outline" full className="mt-4 mb-6" />

      {/* Card list – 12px radius */}
      <Card className="mt-3 relative z-10 overflow-hidden">
        {filtered.map((t, idx) => {
          const checked = selected === t;
          return (
            <div key={`${t}-${idx}`}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggle(t)}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") && toggle(t)
                }
                className="w-full flex items-center"
                style={{
                  WebkitTapHighlightColor: "transparent",
                  touchAction: "manipulation",
                }}
              >
                <div className="flex-1 pl-5 pr-4 py-4 text-left">
                  <span className="text-body-large text-[rgb(var(--clr-on-surface,4,8,18))]">
                    {getBloodTypeDisplayLabel(t, languageCode)}
                  </span>
                </div>

                <div className="mr-4">
                  <Checkbox
                    checked={checked}
                    onChange={(next) => {
                      if (next !== checked) toggle(t);
                    }}
                  />
                </div>
              </div>

              {idx < filtered.length - 1 && (
                <Divider inset variant="outlineVariant" />
              )}
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="px-5 py-6 text-body-medium text-on-surface-var">
            {t("settings.subflows.bloodType.noResults", isFrench ? "Aucun résultat." : "No results.")}
          </div>
        )}
      </Card>

      {/* Unsaved-changes confirm */}
      {showConfirmLeave && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setShowConfirmLeave(false)}
          />
          <div
            className="relative w-[312px] max-w-[560px] min-w-[288px] bg-[var(--Schemes-Surface,#FFFFFF)] rounded-[28px] overflow-hidden
                       shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.06),0px_6px_12px_-4px_rgba(29,79,215,0.06),0px_3px_6px_-2px_rgba(26,27,35,0.06),0px_3px_3px_-1px_rgba(26,27,35,0.06),0px_1.5px_0px_-0.5px_rgba(26,27,35,0.06),0px_0px_0px_1px_rgba(24,50,119,0.10)]"
          >
            <div className="px-6 pt-6">
              <div className="text-headline-small text-on-surface">
                {t("settings.subflows.discardTitle", isFrench ? "Abandonner les modifications ?" : "Discard changes?")}
              </div>
              <div className="text-body-medium text-on-surface-var mt-4">
                {t(
                  "settings.subflows.bloodType.discardBody",
                  isFrench
                    ? "Vous avez des modifications du groupe sanguin non enregistrées. Voulez-vous fermer sans enregistrer ?"
                    : "You have unsaved blood type changes. Are you sure you want to close without saving?"
                )}
              </div>
            </div>

            <div className="p-4 flex gap-3">
              <button
                className="flex-1 rounded-xl px-6 py-3 text-label-large whitespace-nowrap"
                style={{
                  background:
                    "color-mix(in oklab, var(--Schemes-On-Surface,#040812) 8%, transparent)",
                  color: "rgb(var(--afrimed-primary))",
                }}
                onClick={() => setShowConfirmLeave(false)}
              >
                {t("common.keepEditing", isFrench ? "Continuer la modification" : "Keep editing")}
              </button>
              <button
                className="flex-1 rounded-xl px-6 py-3 text-label-large whitespace-nowrap"
                style={{
                  background:
                    "color-mix(in oklab, var(--Schemes-Critical,#F8533A) 12%, transparent)",
                  color: "var(--Schemes-Critical,#F8533A)",
                }}
                onClick={() => {
                  setShowConfirmLeave(false);
                  nav(PATIENT_SETTINGS_PROFILE_ROUTE);
                }}
              >
                {t("common.discard", isFrench ? "Abandonner" : "Discard")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global “Changes saved” Snackbar */}
      <Snackbar open={saved} onClose={() => setSaved(false)} duration={2200}>
        {t("common.changesSaved", isFrench ? "Modifications enregistrées" : "Changes saved")}
      </Snackbar>
    </SettingsScreenShell>
  );
}
